import { localList, uuid as newUuid, evaluate, NetworkError } from '@addictivegroup/addictive-offline'
import { api, ApiError } from './http.js'
import { offline } from './offline.js'
import { entityPath } from './entities.js'
import { channelOf, partnerOf } from '@/lib/channels.js'

/**
 * Records, online first with the replica behind (D-35). Every function answers the same
 * shape whichever side it came from, plus `source` ('server' | 'replica').
 */

// ---------------------------------------------------------------- reading lists

/**
 * @param {string} entity
 * @param {{filter?: object, sort?: string, page?: {number?: number, size?: number}}} params
 * @param {{me?: object, views?: object[], signal?: AbortSignal}} context
 */
export async function listRecords(entity, params = {}, context = {}) {
  try {
    const body = await api(`/${entityPath(entity)}`, { query: params, timeoutMs: 15000 })
    offline.state.online = true
    return { data: body.data, meta: body.meta, source: 'server' }
  } catch (error) {
    if (!(error instanceof NetworkError)) throw error
    offline.state.online = false
    const filter = { ...(params.filter || {}) }
    const view = filter.view ? (context.views || []).find((v) => v.uuid === filter.view) : null
    if (view) {
      Object.assign(filter, view.filters || {}, { ...filter, view: undefined })
    }
    const result = await localList(offline, entity, {
      q: filter.q || '',
      predicate: localPredicate(entity, filter, context.me),
      sort: params.sort || '',
      page: params.page?.number || 1,
      size: params.page?.size || 25,
    })
    return { ...result, source: 'replica', stale: offline.state.stale }
  }
}

const RANGE_KEYS = ['min', 'max', 'from', 'to']

/** The list filters of the API, evaluated on replica rows. */
export function localPredicate(entity, filter, me) {
  const tests = []
  for (const [key, raw] of Object.entries(filter || {})) {
    if (raw === undefined || raw === null || raw === '' || ['q', 'view', 'deleted'].includes(key)) continue
    if (key === 'condition') {
      const condition = typeof raw === 'string' ? JSON.parse(raw) : raw
      tests.push((row) => evaluate(condition, row))
      continue
    }
    if (key === 'owner') {
      tests.push((row) => ownerMatches(row, String(raw), me))
      continue
    }
    if (key === 'regarding') {
      const [type, uuid] = String(raw).split(':')
      tests.push((row) => {
        const target = row.regarding || row.noteable
        return (row.regarding_type === type || row.noteable_type === type || target?.entity === type) && (row.regarding_uuid === uuid || row.noteable_uuid === uuid || target?.uuid === uuid)
      })
      continue
    }
    // Contacts of an account: their primary one, or one they are linked to.
    if (key === 'works_at') {
      tests.push((row) => row.account?.uuid === raw || row.account_uuid === raw || (row.accounts || []).some((a) => a.uuid === raw))
      continue
    }
    // D-198: any of the addresses, or the one playing a role (its own or a stand-in's).
    if (key === 'address') {
      const parts = Object.entries(raw || {}).filter(([k, v]) => k !== 'role' && v)
      if (!parts.length) continue
      const fold = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ł/gi, 'l').toLowerCase()
      const fits = (a) => !!a && parts.every(([k, v]) => (k === 'country' ? String(a.country || '').toUpperCase() === String(v).toUpperCase() : k === 'postal_code' ? String(a.postal_code || '').startsWith(v) : fold(a[k]).includes(fold(v))))
      tests.push((row) => {
        const own = row.addresses || []
        if (!raw.role) return own.some(fits) || fits(row.address)
        const played = row.address_roles?.[raw.role]
        const address = played && own.find((a) => a.uuid === played.address_uuid)
        // A contact's role its account plays: offline only the main address is at hand.
        return address ? fits(address) : played?.via === 'account' && raw.role === 'headquarters' ? fits(row.address) : false
      })
      continue
    }
    // D-226: an opportunity's channel and partner, which no single field holds.
    if (entity === 'opportunity' && (key === 'channel' || key === 'partner')) {
      const wanted = (Array.isArray(raw) ? raw : String(raw).split(',')).map(String)
      tests.push((row) => wanted.includes(String((key === 'channel' ? channelOf : partnerOf)(row))))
      continue
    }
    if (key === 'tag') {
      const wanted = String(raw).split(',')
      tests.push((row) => (row.tags || []).some((t) => wanted.includes(t.uuid)))
      continue
    }
    const read = (row) => {
      if (key.startsWith('cf.')) return row.custom_fields?.[key.slice(3)]
      if (key.endsWith('_uuid')) return row[key]
      const value = row[key]
      return value && typeof value === 'object' && 'uuid' in value ? value.uuid : row[`${key}_uuid`] ?? value
    }
    if (raw && typeof raw === 'object' && !Array.isArray(raw) && Object.keys(raw).some((k) => RANGE_KEYS.includes(k))) {
      tests.push((row) => {
        const v = read(row)
        if (v === null || v === undefined || v === '') return false
        const cmp = (a, b) => (typeof a === 'number' ? a - Number(b) : String(a).localeCompare(String(b)))
        if (raw.min !== undefined && raw.min !== '' && cmp(v, raw.min) < 0) return false
        if (raw.max !== undefined && raw.max !== '' && cmp(v, raw.max) > 0) return false
        if (raw.from && String(v).slice(0, 10) < String(raw.from).slice(0, 10)) return false
        if (raw.to && String(v).slice(0, 10) > String(raw.to).slice(0, 10)) return false
        return true
      })
      continue
    }
    const wanted = (Array.isArray(raw) ? raw : String(raw).split(',')).map(String)
    tests.push((row) => {
      const v = read(row)
      if (typeof v === 'boolean') return wanted.includes(v ? 'true' : 'false') || wanted.includes(v ? '1' : '0')
      if (typeof v === 'string' && !/^[0-9a-f-]{36}$/.test(v) && wanted.length === 1 && !['status', 'type', 'source', 'priority', 'stage'].includes(key)) {
        return v.toLowerCase().includes(wanted[0].toLowerCase())
      }
      return wanted.includes(String(v))
    })
  }
  return (row) => tests.every((t) => t(row))
}

function ownerMatches(row, owner, me) {
  const uuid = row.owner?.uuid
  if (!me) return true
  if (owner === 'me') return row.owner?.type !== 'group' && uuid === me.uuid
  if (owner === 'my_groups') return row.owner?.type === 'group' && (me.groups || []).some((g) => g.uuid === uuid)
  if (owner === 'team') return true // the replica already holds only what the device may keep
  if (owner.startsWith('group:')) return row.owner?.type === 'group' && uuid === owner.slice(6)
  return uuid === owner
}

// ---------------------------------------------------------------- reading one

// A record just created on its own card: the card shows it at once while it is read again.
const primed = new Map()
export function primeRecord(entity, record) {
  if (record?.uuid) primed.set(`${entity}:${record.uuid}`, record)
}
export function takePrimed(entity, uuid) {
  const key = `${entity}:${uuid}`
  const record = primed.get(key) || null
  primed.delete(key)
  return record
}

export async function getRecord(entity, uuid, { include } = {}) {
  const result = await offline.read(
    async () => (await api(`/${entityPath(entity)}/${uuid}`, { query: include ? { include } : undefined, timeoutMs: 12000 })).data,
    async () => {
      const row = await offline.localGet(entity, uuid)
      if (!row) throw new ApiError(404, { message: 'offline-missing', code: 'OFFLINE_MISSING' })
      return row
    },
  )
  return result
}

/** A sub-resource of a record (timeline, history, shares…), online only; `fallback` when offline. */
export async function getRelated(entity, uuid, what, query, fallback = null) {
  try {
    const body = await api(`/${entityPath(entity)}/${uuid}/${what}`, { query, timeoutMs: 15000 })
    return { data: body.data, meta: body.meta, body, source: 'server' }
  } catch (error) {
    if (!(error instanceof NetworkError) || fallback === null) throw error
    return { data: typeof fallback === 'function' ? await fallback() : fallback, source: 'replica' }
  }
}

// ---------------------------------------------------------------- writing

/**
 * @returns {Promise<{status: 'saved'|'queued'|'conflict'|'invalid'|'forbidden'|'error', data?: any, errors?: object, message?: string, uuid: string}>}
 */
export async function saveRecord(entity, uuid, body, { baseVersion = null, label } = {}) {
  const creating = !uuid
  const id = uuid || newUuid()
  const result = await offline.save({
    entity,
    uuid: id,
    method: creating ? 'POST' : 'PATCH',
    path: creating ? `/${entityPath(entity)}` : `/${entityPath(entity)}/${id}`,
    body: creating ? { ...body, uuid: id } : body,
    baseVersion: creating ? null : baseVersion,
    label,
  })
  return { ...result, uuid: id, record: result.data?.data ?? null }
}

export async function deleteRecord(entity, uuid, { label } = {}) {
  const result = await offline.save({ entity, uuid, method: 'DELETE', path: `/${entityPath(entity)}/${uuid}`, body: null, kind: 'delete', label })
  return { ...result, uuid }
}

/** A domain action (qualify, close, complete…) through the same queue as edits. */
export async function recordAction(entity, uuid, action, body = {}, { label, baseVersion = null } = {}) {
  const result = await offline.save({
    entity,
    uuid,
    method: 'POST',
    path: `/${entityPath(entity)}/${uuid}/${action}`,
    body,
    kind: 'update',
    fields: [action],
    baseVersion,
    label,
  })
  return { ...result, uuid, record: result.data?.data ?? null }
}

/** Online-only operations (bulk, shares, configuration): plain API calls. */
export { api }

/** Up to `max` records over several pages (boards, maps, calendars). */
export async function listAll(entity, params = {}, context = {}, max = 500) {
  const size = 100
  let page = 1
  let out = []
  let source = 'server'
  for (;;) {
    const res = await listRecords(entity, { ...params, page: { number: page, size } }, context)
    source = res.source
    out = out.concat(res.data)
    if (out.length >= max || page >= (res.meta?.last_page || 1)) break
    page++
  }
  return { data: out.slice(0, max), source }
}
